// Utility functions for authentication

const SYNC_KEYS = [
  "accessToken",
  "user",
  "profilePictureUrl",
  "authToken",
  // Add any other keys you want to sync
];

// Sync localStorage to sessionStorage if sessionStorage is empty
export const syncLocalToSession = () => {
  SYNC_KEYS.forEach(key => {
    const value = localStorage.getItem(key);
    if (value && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, value);
    }
  });
};

export const getAccessToken = () => {
  syncLocalToSession();
  return sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("accessToken");
};

export const getAuthToken = () => {
  syncLocalToSession();
  return sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
};



export const setAccessToken = (token) => {
  localStorage.setItem("accessToken", token);
  sessionStorage.setItem("accessToken", token);
};

export const setAuthToken = (token) => {
  localStorage.setItem("authToken", token);
  sessionStorage.setItem("authToken", token);
};


export const getUser = () => {
  syncLocalToSession();
  const userStr = sessionStorage.getItem("user") || localStorage.getItem("user") || localStorage.getItem("user");
  return userStr ? JSON.parse(userStr) : null;
};

export const setUser = (user) => {
  const userStr = JSON.stringify(user);
  localStorage.setItem("user", userStr);
  sessionStorage.setItem("user", userStr);
};

export const clearAuth = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("authToken");
  sessionStorage.removeItem("user");
};

// Clear everything from both storages (use on full logout)
export const clearAllStorage = () => {
  try {
    sessionStorage.clear();
    localStorage.clear();
  } catch (e) {
    console.warn('Failed to clear storage', e);
  }
};

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// Device-level preferences that must survive logout; everything else belongs to the logged-in user
const KEEP_AFTER_LOGOUT = ["rememberMe", "rememberedEmail", "termsAccepted", "deletedMsgKeys"];

const setInBothStorages = (key, value) => {
  sessionStorage.setItem(key, value);
  localStorage.setItem(key, value);
};

export const getStoredToken = () =>
  sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");

// Every login method (email, mobile OTP, Google, signup) must start from a clean slate so no data
// from a previous account on this browser leaks into the new session
export const startUserSession = ({ token, user, profilePictureUrl }) => {
  clearUserSession();
  setInBothStorages("accessToken", token);
  if (user) {
    setInBothStorages("user", JSON.stringify(user));
    if (user.id) setInBothStorages("userID", String(user.id));
  }
  if (profilePictureUrl) setInBothStorages("profilePictureUrl", profilePictureUrl);
  window.dispatchEvent(new CustomEvent("profilePictureUpdated"));
};

// Login (email, mobile OTP, Google) writes the token and user data to both storages, so both must be cleared
export const clearUserSession = () => {
  invalidateOwnProfile();
  try {
    const kept = KEEP_AFTER_LOGOUT
      .map((key) => [key, localStorage.getItem(key)])
      .filter(([, value]) => value !== null);
    sessionStorage.clear();
    localStorage.clear();
    kept.forEach(([key, value]) => localStorage.setItem(key, value));
  } catch (e) {
    console.warn("Failed to clear session", e);
  }
};

// The logged-in user's own profile is kept in memory (this tab only, never in storage) and shared by every
// page that loads it. It is dropped on login/logout, after any request that can change the profile (see
// installOwnProfileInvalidation), and after OWN_PROFILE_CACHE_MS so changes made by others (e.g. follower
// counts) still show up.
const OWN_PROFILE_CACHE_MS = 2 * 60 * 1000;
let ownProfileRequest = null;

export const invalidateOwnProfile = () => {
  ownProfileRequest = null;
};

export const fetchOwnProfile = (token) => {
  const now = Date.now();
  if (ownProfileRequest && ownProfileRequest.token === token && now - ownProfileRequest.startedAt < OWN_PROFILE_CACHE_MS) {
    return ownProfileRequest.promise.then(cloneProfileResponse);
  }

  const promise = fetch(`${API_BASE_URL}/user/profile`, {
    method: "GET",
    headers: { token: `${token}`, "Content-Type": "application/json" },
  }).then(async (response) => ({
    ok: response.ok,
    status: response.status,
    body: await response.json().catch(() => ({})),
  }));

  const entry = { token, startedAt: now, promise };
  ownProfileRequest = entry;
  // Never keep a failed load: the next caller retries
  promise.then(
    (result) => {
      if (!result.ok && ownProfileRequest === entry) ownProfileRequest = null;
    },
    () => {
      if (ownProfileRequest === entry) ownProfileRequest = null;
    }
  );
  return promise.then(cloneProfileResponse);
};

// Requests that can change the logged-in user's own profile data (details, pictures, privacy, password,
// verification, follows, seller profile, interests). Read-style POSTs such as get-friends/get-all-users are excluded.
const PROFILE_AFFECTING_PATH = /\/(user|auth|verification|friend|seller|userinterest)\//i;
const isProfileMutation = (method, url) => {
  const verb = String(method || "GET").toUpperCase();
  if (verb === "GET" || verb === "HEAD" || verb === "OPTIONS" || !url) return false;
  let path;
  try {
    path = new URL(String(url), window.location.origin).pathname;
  } catch {
    return false;
  }
  if (!PROFILE_AFFECTING_PATH.test(path)) return false;
  const lastSegment = path.split("/").filter(Boolean).pop() || "";
  return !/^get/i.test(lastSegment) && !/search/i.test(lastSegment);
};

// Install once at startup: drops the cached own profile when a profile-changing request finishes,
// whichever page sent it and whether it used fetch or axios
export const installOwnProfileInvalidation = (axiosInstance) => {
  if (window.__ownProfileInvalidationInstalled) return;
  window.__ownProfileInvalidationInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const url = typeof input === "string" ? input : input?.url;
    const method = init?.method || (typeof input === "string" ? "GET" : input?.method);
    const request = originalFetch(input, init);
    if (isProfileMutation(method, url)) request.then(invalidateOwnProfile, invalidateOwnProfile);
    return request;
  };

  if (axiosInstance?.interceptors) {
    const check = (config) => {
      if (!config) return;
      const url = config.baseURL && !/^https?:/i.test(config.url || "") ? `${config.baseURL}${config.url || ""}` : config.url;
      if (isProfileMutation(config.method, url)) invalidateOwnProfile();
    };
    axiosInstance.interceptors.response.use(
      (response) => {
        check(response.config);
        return response;
      },
      (error) => {
        check(error?.config);
        return Promise.reject(error);
      }
    );
  }
};

// Each caller gets its own Response-like object (ok/status/json) and a private copy of the body
const cloneProfileResponse = ({ ok, status, body }) => ({
  ok,
  status,
  json: async () => JSON.parse(JSON.stringify(body)),
});

export const performLogout = async () => {
  const token =
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessTokenMobile") ||
    localStorage.getItem("accessTokenMobile");

  if (token) {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "GET",
        headers: { token, "Content-Type": "application/json" },
      });
    } catch (e) {
      console.error("Logout API request failed", e);
    }
  }

  clearUserSession();
  // Full reload so no in-memory user state survives, and Back cannot return to a logged-in page
  window.location.replace("/login");
};

// Logout helper that clears storage and optionally redirects
export const logout = (navigate) => {
  clearAllStorage();
  if (typeof navigate === 'function') {
    try {
      navigate('/login');
      return;
    } catch (e) {
      // ignore
    }
  }
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
};

// Utility functions for authentication






// ...existing code...